import { StandardEvents } from '@shopify/events';

const DRAWER = 'theme-drawer#cart-drawer';
const cartDrawer = () => document.querySelector(DRAWER);
const nextPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

function installCartUX() {
  const cart = cartDrawer();
  if (!cart || cart.dataset.baseformCartUx === '7') return;

  cart.dataset.baseformCartUx = '7';
  cart.setAttribute('no-persist', '');

  const style = document.createElement('style');
  style.textContent = `
    @media (min-width:990px){
      html:has(${DRAWER}[open]) .page-wrapper--drawer-open{margin-inline-end:0!important}
      html:has(${DRAWER}[open]) .page-wrapper{overflow:hidden!important}
      .baseform-cart-backdrop{position:fixed;inset:0;z-index:var(--layer-sticky);background:rgb(0 0 0/38%);opacity:0;pointer-events:none;transition:opacity 120ms ease}
      .baseform-cart-backdrop.is-visible{opacity:1;pointer-events:auto}
    }
    @media (max-width:989px){.baseform-cart-backdrop{display:none!important}}

    ${DRAWER} .baseform-cart-optimistic{position:absolute;z-index:26;display:flex;align-items:flex-start;gap:14px;background:var(--color-background);pointer-events:none}
    ${DRAWER} .baseform-cart-optimistic--overlay{inset:64px 0 0;padding:22px}
    ${DRAWER} .baseform-cart-optimistic--inline{top:64px;left:0;right:0;padding:12px 18px;border-bottom:1px solid rgb(var(--color-foreground-rgb)/10%);box-shadow:0 8px 22px rgb(0 0 0/8%)}
    ${DRAWER} .baseform-cart-optimistic__media{width:72px;height:86px;flex:0 0 72px;border-radius:8px;background:rgb(var(--color-foreground-rgb)/7%);overflow:hidden}
    ${DRAWER} .baseform-cart-optimistic--inline .baseform-cart-optimistic__media{width:42px;height:50px;flex-basis:42px}
    ${DRAWER} .baseform-cart-optimistic__media img{width:100%;height:100%;object-fit:cover;display:block}
    ${DRAWER} .baseform-cart-optimistic__copy{min-width:0;flex:1;padding-top:2px}
    ${DRAWER} .baseform-cart-optimistic__title{margin:0 0 8px;font:inherit;font-weight:600;line-height:1.3;color:var(--color-foreground)}
    ${DRAWER} .baseform-cart-optimistic--inline .baseform-cart-optimistic__title{margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    ${DRAWER} .baseform-cart-optimistic__status{display:inline-flex;align-items:center;gap:8px;font-size:.9em;opacity:.72}
    ${DRAWER} .baseform-cart-spinner,${DRAWER} .baseform-cart-optimistic__spinner,${DRAWER}[data-baseform-busy] .cart__checkout-button::after{width:16px;height:16px;border:2px solid rgb(var(--color-foreground-rgb)/18%);border-top-color:currentColor;border-radius:50%;box-sizing:border-box;animation:baseform-spin 650ms linear infinite}
    ${DRAWER} .baseform-cart-optimistic__spinner{width:14px;height:14px}
    ${DRAWER} .baseform-cart-optimistic__error{font-size:.9em;line-height:1.4}

    ${DRAWER} .baseform-cart-pending{position:absolute;inset:64px 0 0;z-index:25;display:none;place-items:center;align-content:center;gap:10px;padding:24px;background:var(--color-background);color:var(--color-foreground);pointer-events:none}
    ${DRAWER}[data-baseform-optimistic-empty] .baseform-cart-pending{display:grid}

    ${DRAWER}[data-baseform-busy] .cart__checkout-button{position:relative;pointer-events:none!important}
    ${DRAWER}[data-baseform-busy] .cart__checkout-button .button-text{opacity:.45}
    ${DRAWER}[data-baseform-busy] .cart__checkout-button::after{content:'';position:absolute;inset:50% auto auto 50%;translate:-50% -50%}
    ${DRAWER}[data-baseform-busy] .additional-checkout-buttons,
    ${DRAWER}[data-baseform-busy] shopify-accelerated-checkout-cart,
    ${DRAWER}[data-baseform-busy] a[href*='/checkout']{pointer-events:none!important;opacity:.62}

    /* Horizon disables the whole component while a cart request is active. Keep line controls live. */
    ${DRAWER} cart-items-component.cart-items-disabled .cart-items__remove,
    ${DRAWER} cart-items-component.cart-items-disabled cart-quantity-selector-component button,
    ${DRAWER} cart-items-component.cart-items-disabled cart-quantity-selector-component input{pointer-events:auto!important}

    /* Prevent discount/note section morphs from racing a line mutation. */
    ${DRAWER}[data-baseform-busy] .cart-actions{pointer-events:none}

    ${DRAWER} .baseform-deferred-remove{display:none!important;pointer-events:none!important}

    @keyframes baseform-spin{to{transform:rotate(360deg)}}
    @media (prefers-reduced-motion:reduce){
      ${DRAWER} .baseform-cart-spinner,
      ${DRAWER} .baseform-cart-optimistic__spinner,
      ${DRAWER}[data-baseform-busy] .cart__checkout-button::after{animation:none}
    }
  `;
  document.head.appendChild(style);

  const backdrop = document.createElement('div');
  backdrop.className = 'baseform-cart-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.addEventListener('click', () => {
    const openDrawers = Array.from(document.querySelectorAll('theme-drawer[open]'));
    const topmost = openDrawers.reduce((current, drawer) => {
      if (!current) return drawer;
      const currentOrder = Number(current.style.getPropertyValue('--drawer-stack-order') || 0);
      const drawerOrder = Number(drawer.style.getPropertyValue('--drawer-stack-order') || 0);
      return drawerOrder > currentOrder ? drawer : current;
    }, null);
    if (topmost === cart) cart.close?.();
  });
  document.body.appendChild(backdrop);

  const pending = document.createElement('div');
  pending.className = 'baseform-cart-pending';
  pending.setAttribute('role', 'status');
  pending.setAttribute('aria-live', 'polite');
  pending.innerHTML = '<span class="baseform-cart-spinner" aria-hidden="true"></span><span>Updating cart…</span>';
  cart.querySelector('.theme-drawer__dialog')?.appendChild(pending);

  const syncBackdrop = () => backdrop.classList.toggle('is-visible', cart.hasAttribute('open'));
  cart.addEventListener('theme-drawer:open', syncBackdrop);
  cart.addEventListener('theme-drawer:close', syncBackdrop);
  syncBackdrop();

  try {
    sessionStorage.removeItem('theme-drawer-open');
  } catch {}

  const activeMutations = new Set();
  const queued = new Map();
  let serial = Promise.resolve();
  let optimisticToken = 0;

  const syncBusy = () => {
    const busy = activeMutations.size > 0 || queued.size > 0;
    cart.toggleAttribute('data-baseform-busy', busy);
    const checkout = cart.querySelector('.cart__checkout-button');
    if (checkout instanceof HTMLButtonElement) {
      if (busy) checkout.setAttribute('aria-disabled', 'true');
      else checkout.removeAttribute('aria-disabled');
    }
  };

  function trackMutation(promise) {
    if (!promise) return;
    const tracked = Promise.resolve(promise).catch(() => {});
    activeMutations.add(tracked);
    syncBusy();
    tracked.finally(() => {
      activeMutations.delete(tracked);
      syncBusy();
    });
  }

  async function waitForNativeQuantityIdle() {
    await customElements.whenDefined('cart-items-component');
    while (true) {
      const C = customElements.get('cart-items-component');
      const jobs = C?.pendingQuantityUpdates ?? [];
      if (!jobs.length) return;
      await Promise.allSettled(jobs);
      await Promise.resolve();
    }
  }

  async function waitForCartIdle() {
    while (true) {
      if (activeMutations.size) await Promise.allSettled([...activeMutations]);
      await waitForNativeQuantityIdle();
      await Promise.resolve();
      if (!activeMutations.size) break;
    }
    await nextPaint();
    if (activeMutations.size) return waitForCartIdle();
    await waitForNativeQuantityIdle();
  }

  function renderedCartMatches(detail) {
    const items = Array.isArray(detail?.items) ? detail.items : null;
    if (!items) return true;

    const desiredTotal = items.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
    const component = cart.querySelector('cart-items-component');

    if (desiredTotal === 0) {
      return Boolean(cart.querySelector('[data-cart-drawer-empty],.cart-drawer--empty'));
    }
    if (!component) return false;

    const countNode = component.querySelector('[ref="cartItemCount"]');
    const renderedTotal = Number.parseInt(countNode?.textContent || '', 10);
    if (Number.isFinite(renderedTotal) && renderedTotal !== desiredTotal) return false;

    const desiredKeys = items.map((item) => String(item?.key || '')).filter(Boolean);
    if (!desiredKeys.length) return Number.isFinite(renderedTotal) ? renderedTotal === desiredTotal : true;

    const renderedKeys = new Set(
      Array.from(component.querySelectorAll('.cart-items__table-row[data-key]')).map((row) => row.dataset.key || '')
    );
    return desiredKeys.every((key) => renderedKeys.has(key));
  }

  function waitForRenderedCart(detail, timeout = 4000) {
    if (renderedCartMatches(detail)) return Promise.resolve();

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const check = () => {
        if (renderedCartMatches(detail)) finish();
      };
      const observer = new MutationObserver(check);
      observer.observe(cart, { childList: true, subtree: true, characterData: true });
      const timer = window.setTimeout(finish, timeout);
      requestAnimationFrame(check);
    });
  }

  function waitForComponentIdle(selector, timeout = 4000) {
    const isIdle = () => !cart.querySelector(`${selector}[aria-busy="true"]`);
    if (isIdle()) return nextPaint();

    return new Promise((resolve) => {
      let done = false;
      let timer = 0;
      const finish = () => {
        if (done) return;
        done = true;
        observer.disconnect();
        if (timer) clearTimeout(timer);
        resolve();
      };
      const observer = new MutationObserver(() => {
        if (isIdle()) finish();
      });
      observer.observe(cart, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-busy'],
      });
      timer = window.setTimeout(finish, timeout);
      requestAnimationFrame(() => {
        if (isIdle()) finish();
      });
    });
  }

  const rows = (component) => Array.from(component.querySelectorAll('.cart-items__table-row[data-key]'));
  const variantId = (row) => row.querySelector('cart-quantity-selector-component[data-variant-id]')?.dataset.variantId || '';

  function liveComponent(item) {
    const component = item?.component;
    if (component?.isConnected) return component;
    const replacement = cart.querySelector('cart-items-component');
    if (item && replacement) item.component = replacement;
    return replacement;
  }

  function resolveRow(component, item) {
    const current = rows(component);
    const exact = current.find((row) => row.dataset.key === item.key);
    if (exact) return exact;
    if (!item.variantId) return null;
    const matches = current.filter((row) => variantId(row) === item.variantId);
    return matches.length === 1 ? matches[0] : null;
  }

  function syncOptimisticEmpty(component) {
    if (cart.querySelector('[data-cart-drawer-empty]')) {
      cart.removeAttribute('data-baseform-optimistic-empty');
      return;
    }
    if (!component) {
      cart.removeAttribute('data-baseform-optimistic-empty');
      return;
    }
    const current = rows(component).filter((row) => !row.dataset.parentKey);
    const visible = current.filter((row) => !row.classList.contains('baseform-deferred-remove'));
    cart.toggleAttribute('data-baseform-optimistic-empty', current.length > 0 && visible.length === 0);
  }

  function entryFor(component, row) {
    const key = row.dataset.key || '';
    const vid = variantId(row);
    const mapKey = key || `${vid}:${rows(component).indexOf(row)}`;
    let item = queued.get(mapKey);
    if (!item) {
      item = {
        mapKey,
        key,
        variantId: vid,
        component,
        quantity: NaN,
        remove: false,
        version: 0,
        scheduled: false,
        removeObserver: null,
        quantityObserver: null,
      };
      queued.set(mapKey, item);
      syncBusy();
    }
    return item;
  }

  function stopQuantityObserver(item) {
    item.quantityObserver?.disconnect();
    item.quantityObserver = null;
  }

  function applyVisibleQuantity(item) {
    if (item.remove || !Number.isFinite(item.quantity)) return;
    const component = liveComponent(item);
    if (!component) return;
    const row = resolveRow(component, item);
    const selector = row?.querySelector('cart-quantity-selector-component');
    const input = selector?.querySelector('input[ref="quantityInput"]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(item.quantity);
    selector?.updateButtonStates?.();
  }

  function watchQuantityIntent(item) {
    if (item.quantityObserver) return;
    const component = liveComponent(item);
    if (!component) return;
    item.quantityObserver = new MutationObserver(() => applyVisibleQuantity(item));
    item.quantityObserver.observe(cart, { childList: true, subtree: true });
  }

  function hideForRemove(component, row, item) {
    stopQuantityObserver(item);

    const apply = (targetComponent, parent) => {
      const targets = [parent, ...rows(targetComponent).filter((candidate) => candidate.dataset.parentKey === parent.dataset.key)];
      for (const target of targets) {
        target.classList.add('baseform-deferred-remove');
        target.setAttribute('aria-hidden', 'true');
      }
      syncOptimisticEmpty(targetComponent);
    };

    apply(component, row);
    item.removeObserver?.disconnect();
    item.removeObserver = new MutationObserver(() => {
      const currentComponent = liveComponent(item);
      if (!currentComponent) return;
      const currentRow = resolveRow(currentComponent, item);
      if (currentRow) apply(currentComponent, currentRow);
      else syncOptimisticEmpty(currentComponent);
    });
    item.removeObserver.observe(cart, { childList: true, subtree: true });
  }

  function restoreRemove(item) {
    item.removeObserver?.disconnect();
    item.removeObserver = null;
    const component = liveComponent(item);
    if (!component) {
      cart.removeAttribute('data-baseform-optimistic-empty');
      return;
    }
    const row = resolveRow(component, item);
    if (row) {
      const targets = [row, ...rows(component).filter((candidate) => candidate.dataset.parentKey === row.dataset.key)];
      for (const target of targets) {
        target.classList.remove('baseform-deferred-remove');
        target.removeAttribute('aria-hidden');
      }
    }
    syncOptimisticEmpty(component);
  }

  function schedule(item) {
    if (item.scheduled) return;
    item.scheduled = true;

    serial = serial
      .catch(() => {})
      .then(async () => {
        let applied = -1;
        while (applied !== item.version) {
          await waitForCartIdle();

          const version = item.version;
          const component = liveComponent(item);
          if (!component || typeof component.updateQuantity !== 'function') return;

          const row = resolveRow(component, item);
          if (!row) return;
          const line = rows(component).indexOf(row) + 1;
          if (line < 1) return;

          if (item.remove) {
            item.removeObserver?.disconnect();
            item.removeObserver = null;
            await component.updateQuantity({ line, quantity: 0, action: 'clear' });
            restoreRemove(item);
            return;
          }

          const quantity = item.quantity;
          if (!Number.isFinite(quantity)) return;

          stopQuantityObserver(item);
          const selector = row.querySelector('cart-quantity-selector-component');
          const input = selector?.querySelector('input[ref="quantityInput"]');
          if (input instanceof HTMLInputElement) input.value = String(quantity);
          selector?.updateButtonStates?.();

          await component.updateQuantity({ line, quantity, action: 'change' });
          applied = version;

          if (applied !== item.version) {
            applyVisibleQuantity(item);
            watchQuantityIntent(item);
          }
        }
      })
      .catch((error) => {
        console.warn('[baseform-cart] queued cart action failed', error);
        if (item.remove) restoreRemove(item);
      })
      .finally(() => {
        item.removeObserver?.disconnect();
        stopQuantityObserver(item);
        queued.delete(item.mapKey);
        syncBusy();
        syncOptimisticEmpty(cart.querySelector('cart-items-component'));
      });
  }

  function queueRemove(component, row) {
    const item = entryFor(component, row);
    item.remove = true;
    item.version += 1;
    hideForRemove(component, row, item);
    schedule(item);
  }

  function queueQuantity(component, row, selector, input, quantity) {
    input.value = String(quantity);
    selector?.updateButtonStates?.();

    const item = entryFor(component, row);
    if (item.remove) return;
    item.quantity = quantity;
    item.version += 1;
    watchQuantityIntent(item);
    schedule(item);
  }

  function normalizedQuantity(input) {
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    const raw = Number.parseInt(input.value, 10);
    const quantity = Math.min(max, Math.max(min, Number.isFinite(raw) ? raw : min));
    return { min, step, quantity };
  }

  /*
   * Own cart-line interactions at the earliest capture phase. Visual state changes
   * immediately; Shopify mutations are serialized in the background so line indexes
   * cannot race after another line is removed.
   */
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const component = target?.closest('cart-items-component');
      if (!component || !cart.contains(component)) return;

      const remove = target.closest('.cart-items__remove');
      if (remove) {
        if (remove.hasAttribute('hidden')) return;
        const row = remove.closest('.cart-items__table-row[data-key]');
        if (!row) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        queueRemove(component, row);
        return;
      }

      const button = target.closest(
        'cart-quantity-selector-component button[name="plus"],cart-quantity-selector-component button[name="minus"]'
      );
      if (!button || button.disabled) return;

      const row = button.closest('.cart-items__table-row[data-key]');
      const selector = button.closest('cart-quantity-selector-component');
      const input = selector?.querySelector('input[ref="quantityInput"]');
      if (!row || !(input instanceof HTMLInputElement) || input.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const current = Number(input.value) || 0;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step) || 1;
      const next = Math.min(max, Math.max(min, current + step * (button.name === 'plus' ? 1 : -1)));
      queueQuantity(component, row, selector, input, next);
    },
    true
  );

  /* Horizon commits typed cart quantity on blur. Capture blur before Horizon so we do
     not create a second debounced native request alongside the optimistic queue. */
  window.addEventListener(
    'blur',
    (event) => {
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      if (!input?.matches('cart-quantity-selector-component input[ref="quantityInput"]')) return;

      const selector = input.closest('cart-quantity-selector-component');
      const component = input.closest('cart-items-component');
      const row = input.closest('.cart-items__table-row[data-key]');
      if (!selector || !component || !row || !cart.contains(component)) return;

      event.stopPropagation();
      event.stopImmediatePropagation();
      input.removeAttribute('data-skip-value-update');
      if (input.disabled) return;

      const { min, step, quantity } = normalizedQuantity(input);
      if ((quantity - min) % step !== 0) {
        input.value = String(quantity);
        input.reportValidity();
        return;
      }

      const currentServerValue = Number.parseInt(input.defaultValue, 10);
      const pendingItem = queued.get(row.dataset.key || `${variantId(row)}:${rows(component).indexOf(row)}`);
      if (!pendingItem && Number.isFinite(currentServerValue) && quantity === currentServerValue) {
        input.value = String(quantity);
        selector?.updateButtonStates?.();
        return;
      }

      queueQuantity(component, row, selector, input, quantity);
    },
    true
  );

  /* Enter should commit a typed quantity, not implicitly submit the cart form. */
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      if (!input?.matches('cart-quantity-selector-component input[ref="quantityInput"]')) return;
      if (!cart.contains(input)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      input.blur();
    },
    true
  );

  /* Keep checkout and cart-action mutations from racing the queued line state. */
  cart.addEventListener(
    'click',
    (event) => {
      if (!cart.hasAttribute('data-baseform-busy')) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[name="checkout"],a[href*="/checkout"],.cart-actions button')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  cart.addEventListener(
    'submit',
    (event) => {
      if (!cart.hasAttribute('data-baseform-busy')) return;
      if (!(event.target instanceof HTMLFormElement)) return;
      if (event.target.id === 'cart-form' || event.target.closest('.cart-actions')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  function getProductPreview(event) {
    const source = event.target instanceof Element ? event.target : null;
    const form = source?.closest('product-form-component') ?? source;
    const card = source?.closest('product-card');
    const image =
      form?.querySelector('add-to-cart-component[data-product-variant-media]')?.dataset.productVariantMedia ||
      card?.querySelector('img')?.currentSrc ||
      card?.querySelector('img')?.src ||
      '';
    const title =
      card?.querySelector('[class*="product-card"] [class*="title"]')?.textContent?.trim() ||
      card?.querySelector('a[href*="/products/"]')?.textContent?.trim() ||
      source?.closest('.shopify-section')?.querySelector('h1')?.textContent?.trim() ||
      'Adding to your cart';
    return { image, title };
  }

  function removeOptimistic(token) {
    if (token !== optimisticToken) return;
    cart.querySelector('.baseform-cart-optimistic')?.remove();
  }

  function showOptimistic(event) {
    const token = ++optimisticToken;
    const wasEmpty = Boolean(cart.querySelector('[data-cart-drawer-empty],.cart-drawer--empty'));
    cart.querySelector('.baseform-cart-optimistic')?.remove();

    const optimistic = document.createElement('div');
    optimistic.className = `baseform-cart-optimistic ${
      wasEmpty ? 'baseform-cart-optimistic--overlay' : 'baseform-cart-optimistic--inline'
    }`;
    optimistic.setAttribute('role', 'status');
    optimistic.setAttribute('aria-live', 'polite');

    const media = document.createElement('div');
    media.className = 'baseform-cart-optimistic__media';
    const { image, title } = getProductPreview(event);
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.decoding = 'async';
      media.appendChild(img);
    }

    const copy = document.createElement('div');
    copy.className = 'baseform-cart-optimistic__copy';
    const heading = document.createElement('p');
    heading.className = 'baseform-cart-optimistic__title';
    heading.textContent = title;
    const status = document.createElement('div');
    status.className = 'baseform-cart-optimistic__status';
    status.innerHTML = '<span class="baseform-cart-optimistic__spinner" aria-hidden="true"></span><span>Adding to cart…</span>';
    copy.append(heading, status);
    optimistic.append(media, copy);
    cart.querySelector('.theme-drawer__dialog')?.appendChild(optimistic);

    let observer = null;
    if (wasEmpty) {
      observer = new MutationObserver(() => {
        if (!cart.querySelector('[data-cart-drawer-empty],.cart-drawer--empty')) {
          removeOptimistic(token);
          observer?.disconnect();
        }
      });
      observer.observe(cart, { childList: true, subtree: true });
    }

    window.setTimeout(() => {
      removeOptimistic(token);
      observer?.disconnect();
    }, 10000);

    return { token, wasEmpty };
  }

  function showAddError(token) {
    if (token !== optimisticToken) return;
    const optimistic = cart.querySelector('.baseform-cart-optimistic');
    if (!optimistic) return;
    const copy = optimistic.querySelector('.baseform-cart-optimistic__copy');
    if (!copy) return;

    copy.replaceChildren();
    const title = document.createElement('p');
    title.className = 'baseform-cart-optimistic__title';
    title.textContent = 'Couldn’t add this item';
    const message = document.createElement('div');
    message.className = 'baseform-cart-optimistic__error';
    message.textContent = 'Please try again.';
    copy.append(title, message);
    window.setTimeout(() => removeOptimistic(token), 3500);
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!['add', 'change', 'update', 'remove'].includes(event.action)) return;

    const settledPromise = event.promise?.then(async (result) => {
      if (!result?.detail?.didError) await waitForRenderedCart(result?.detail);
      return result;
    });
    trackMutation(settledPromise ?? event.promise);

    if (event.action !== 'add') return;

    const modal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (modal?.open) {
      settledPromise
        ?.then(({ detail }) => {
          if (detail?.didError) return;
          const open = () => {
            if (!cart.hasAttribute('open')) cart.open?.();
          };
          if (modal.open) modal.addEventListener('close', open, { once: true });
          else open();
        })
        .catch(() => {});
      return;
    }

    const { token, wasEmpty } = showOptimistic(event);
    if (!cart.hasAttribute('open')) cart.open?.();
    settledPromise
      ?.then(({ detail }) => {
        if (detail?.didError) {
          showAddError(token);
          return;
        }
        if (!wasEmpty) nextPaint().then(() => removeOptimistic(token));
      })
      .catch(() => showAddError(token));
  });

  document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
    const settledPromise = event.promise?.then(async (result) => {
      await waitForComponentIdle('cart-discount-component');
      return result;
    });
    trackMutation(settledPromise ?? event.promise);
  });
  document.addEventListener(StandardEvents.cartNoteUpdate, (event) => trackMutation(event.promise));
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({ eventTarget: () => cartDrawer() ?? document });
  actions.openCart.configure({
    async handler() {
      const cart = cartDrawer();
      if (cart?.open) cart.open();
      else window.location.href = Theme.routes.cart_url || '/cart';
    },
  });

  installCartUX();
}

function boot(attempt = 0) {
  if (window.Shopify?.actions) {
    init();
    return;
  }
  if (attempt < 100) window.setTimeout(() => boot(attempt + 1), 50);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
} else {
  boot();
}
